import { useCallback, useEffect, useRef } from "react";
import { VirtualTable, VirtualTableProps } from "./virtual-table";

interface InfiniteTableProps<TData> extends VirtualTableProps<TData> {
  fetchMoreDataThreshold?: number;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetching: boolean;
}

export const InfiniteTable = <TData,>({
  fetchMoreDataThreshold = 500,
  fetchNextPage,
  hasNextPage,
  isFetching,
  ...props
}: InfiniteTableProps<TData>) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement: HTMLDivElement | null) => {
      if (!containerRefElement) {
        return;
      }

      const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
      if (
        scrollHeight - scrollTop - clientHeight < fetchMoreDataThreshold &&
        !isFetching &&
        hasNextPage
      ) {
        fetchNextPage();
      }
    },
    [fetchMoreDataThreshold, fetchNextPage, hasNextPage, isFetching],
  );

  useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current);
  }, [fetchMoreOnBottomReached]);

  return (
    <VirtualTable
      ref={tableContainerRef}
      onScroll={(e) => fetchMoreOnBottomReached(e.currentTarget)}
      {...props}
    />
  );
};
